// No-Service-Worker interstitial (task).
//
// Some browsing contexts — iOS in-app WKWebViews launched from
// third-party apps (X, Instagram, Facebook, Line, TikTok, Snapchat,
// Slack, …), and any other surface where `navigator.serviceWorker`
// is absent — can never load a p2claw app: the SW is a hard
// requirement.
//
// Previously the wrapper threw `"browser has no Service Worker
// support"` and the user saw an unactionable red error. The fix is
// to render a dedicated page that:
//
//   * Says the link needs the user's real browser.
//   * Shows `location.href` in a large monospaced block so the user
//     can long-press → Copy (the native iOS gesture).
//   * Offers a Copy button using `navigator.clipboard.writeText` —
//     clipboard writes DO work in WKWebView even though SW does not.
//
// The copy is deliberately browser- and platform-neutral. We don't
// try to detect which app the user is in, name a specific browser,
// or display a screenshot of a share menu: those affordances drift
// silently as app versions ship, and the durable escape route is
// just "copy the link, open your browser, paste."

/** DOM id used for the interstitial root. Exported for tests +
 *  any caller that wants to feature-detect the overlay. */
export const IN_APP_WEBVIEW_PAGE_ID = "p2claw-in-app-webview-page";

export interface RenderInAppWebviewPageOptions {
  /** Current navigation URL — rendered verbatim in the monospaced
   *  block. Defaults to `window.location.href`. */
  currentUrl?: string;
  /** Override for testing. Defaults to `document`. */
  doc?: Document;
  /** Override for testing. Defaults to `navigator.clipboard`. Pass
   *  `null` to opt out (the user can still long-press → Copy via the
   *  native gesture on the selectable URL block). */
  clipboard?: Pick<Clipboard, "writeText"> | null;
}

/**
 * Render the interstitial into `document.body`. Idempotent: a
 * second call replaces the previous render in place. The page is
 * fixed/inset:0 above the iframe and the boot status line, both of
 * which it deliberately hides.
 */
export function renderInAppWebviewPage(
  opts: RenderInAppWebviewPageOptions = {},
): void {
  const doc = opts.doc ?? document;
  const currentUrl = opts.currentUrl ?? globalThis.location?.href ?? "";
  const clipboard =
    opts.clipboard === undefined
      ? (globalThis.navigator?.clipboard ?? null)
      : opts.clipboard;

  // Clear the iframe + status surface so the only visible chrome is
  // this page. Duck-typed assignment (mirroring `showBoxErrorPage`)
  // so tests can pass a minimal fake document.
  const iframe = doc.getElementById("p2claw-app");
  if (iframe) (iframe as unknown as { hidden: boolean }).hidden = true;
  const statusEl = doc.getElementById("p2claw-status");
  if (statusEl) statusEl.textContent = "";

  // Replace any previous instance — simpler than diffing children.
  doc.getElementById(IN_APP_WEBVIEW_PAGE_ID)?.remove();

  const page = doc.createElement("div");
  page.id = IN_APP_WEBVIEW_PAGE_ID;
  page.setAttribute("role", "alert");
  page.style.cssText = [
    "position:fixed",
    "inset:0",
    "background:#fff",
    "color:#222",
    "font:15px/1.5 system-ui,-apple-system,sans-serif",
    "padding:3em 1em",
    "z-index:2147483646",
    "overflow:auto",
    "-webkit-user-select:text",
    "user-select:text",
  ].join(";");

  const inner = doc.createElement("div");
  inner.style.cssText = "max-width:32em;margin:0 auto";

  const h1 = doc.createElement("h1");
  h1.textContent = "Open this link in your browser";
  h1.style.cssText =
    "font:600 1.3em/1.3 system-ui,-apple-system,sans-serif;margin:0 0 .5em";
  inner.appendChild(h1);

  const lead = doc.createElement("p");
  lead.setAttribute("data-role", "instruction");
  lead.textContent = "Tap to copy, then open your browser and paste.";
  lead.style.cssText = "margin:0 0 1.25em";
  inner.appendChild(lead);

  // Copyable URL block — large, monospaced, selectable, with the
  // long-press-friendly affordance baked into the styling so it
  // looks like a value the user is meant to copy.
  const urlBlock = doc.createElement("div");
  urlBlock.setAttribute("data-role", "url");
  urlBlock.textContent = currentUrl;
  urlBlock.style.cssText = [
    "font:13px/1.45 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
    "padding:14px 16px",
    "background:#f4f4f6",
    "border:1px solid #d0d0d6",
    "border-radius:8px",
    "word-break:break-all",
    "-webkit-user-select:all",
    "user-select:all",
    "margin:0 0 1em",
    "cursor:text",
  ].join(";");
  inner.appendChild(urlBlock);

  // Copy button. The clipboard API works in WKWebView even though
  // SW does not — confirmed by WK docs.
  const button = doc.createElement("button");
  button.type = "button";
  button.setAttribute("data-role", "copy");
  button.textContent = "Copy link";
  button.style.cssText = [
    "appearance:none",
    "-webkit-appearance:none",
    "display:inline-block",
    "padding:10px 18px",
    "font:600 14px/1 system-ui,-apple-system,sans-serif",
    "background:#222",
    "color:#fff",
    "border:0",
    "border-radius:8px",
    "cursor:pointer",
    "margin:0 0 1.5em",
  ].join(";");
  inner.appendChild(button);

  // Toast that flashes after a successful copy. Lives next to the
  // button so screen readers announce it via aria-live.
  const toast = doc.createElement("span");
  toast.setAttribute("data-role", "toast");
  toast.setAttribute("role", "status");
  toast.setAttribute("aria-live", "polite");
  toast.style.cssText = [
    "display:inline-block",
    "margin-left:.75em",
    "padding:6px 10px",
    "font-size:.85em",
    "color:#1a6f3c",
    "background:#dff5e6",
    "border-radius:6px",
    "opacity:0",
    "transition:opacity 120ms ease-out",
  ].join(";");
  toast.textContent = "Copied";
  inner.appendChild(toast);

  button.addEventListener("click", () => {
    void copyAndFlashToast(currentUrl, clipboard, toast);
  });

  const fineprint = doc.createElement("small");
  fineprint.textContent =
    "p2claw links are peer-to-peer and rely on browser APIs this view doesn't support.";
  fineprint.style.cssText = "color:#888";
  inner.appendChild(fineprint);

  page.appendChild(inner);
  doc.body.appendChild(page);
}

async function copyAndFlashToast(
  url: string,
  clipboard: Pick<Clipboard, "writeText"> | null,
  toast: HTMLElement,
): Promise<void> {
  if (!clipboard) {
    // No clipboard API: leave the URL block selected — the user
    // can still long-press → Copy via the native gesture.
    return;
  }
  try {
    await clipboard.writeText(url);
    toast.style.opacity = "1";
    // Fade after ~1.5s. We don't pin a setTimeout id because the
    // page is single-shot — there's no path off this view except
    // navigating away.
    setTimeout(() => {
      toast.style.opacity = "0";
    }, 1500);
  } catch {
    // Clipboard rejected (rare on iOS — usually only in cross-origin
    // iframes, not at the top level). Silently fall back to the
    // selectable URL block.
  }
}
